import '.a{display:grid;}.ssr-plugin'
import '.b{grid-template-columns:minmax(0, 1fr);}.ssr-plugin'
import '.c{gap:1.5rem;}.ssr-plugin'
import { PageLayout } from '../layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { BlogCard } from '@app/components/blocks/card/BlogCard'
import { styled } from 'inlines'
import { useCallback, useEffect, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import { Blog } from '@schema/blog'
import { useInfiniteScroll } from '@app/util/useInfiniteScroll'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { useLocation, useRoute } from 'wouter'
import { pages } from '@app/util/links'
import useMediaQuery from '@app/util/useMediaQuery'
import { useTranslations } from '@i18n/useLanguage'
import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'
import { match } from 'ts-pattern'

export const BlogsPage = () => {
  const [, params] = useRoute<{ blogType: string }>(pages.blogs(':blogType'))
  const [, setLocation] = useLocation()
  const isMobile = useMediaQuery()
  const t = useTranslations()
  const activeTab = (params?.blogType as 'verhalen' | 'news' | 'blog') || 'blog'
  const activeTabLabel = match(activeTab)
    .with('verhalen', () => t.app.blogPage.tabs.stories)
    .with('news', () => t.app.blogPage.tabs.news)
    .with('blog', () => t.app.blogPage.tabs.blog)
    .exhaustive()

  return (
    <PageLayout gap={0}>
      <StaticPageSeo
        path={pages.blogs(activeTab)}
        pageTitle={t.app.blogPage.title}
        tabTitle={activeTabLabel}
        description={t.app.blogPage.description}
        useSiteDefaultImage
      />
      <Title.H1 style={{ textAlign: 'center' }}>
        {t.app.blogPage.title}
      </Title.H1>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: isMobile ? '1rem' : '2rem',
        }}
      >
        <TabsGroup
          selectedTab={activeTab}
          onSelectTab={(key) => setLocation(pages.blogs(key))}
          tabs={[
            { value: 'verhalen', label: t.app.blogPage.tabs.stories },
            { value: 'news', label: t.app.blogPage.tabs.news },
            { value: 'blog', label: t.app.blogPage.tabs.blog },
          ]}
        />
      </div>

      <BlogItemsList
        key={params?.blogType || 'blog'}
        blogType={(params?.blogType as 'verhalen' | 'news' | 'blog') || 'blog'}
      />
    </PageLayout>
  )
}

const blogTypeToQueryMapping = {
  verhalen: 'list-stories',
  news: 'list-news',
  blog: 'list-blog',
}

const PAGE_SIZE = 9

export const BlogItemsList = ({
  blogType,
}: {
  blogType: 'verhalen' | 'news' | 'blog'
}) => {
  const [blogItems, setBlogItems] = useState<Blog[]>([])
  const [top, setTop] = useState(PAGE_SIZE)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isMobile = useMediaQuery()
  const t = useTranslations()

  const queryName = blogTypeToQueryMapping[blogType]
  const dataServerQuery = useQuery(queryName, { top: PAGE_SIZE })
  const countServerQuery = useQuery(queryName, { count: true })
  const isLoading = useLoading()
  const serverBlogItems: Blog[] = dataServerQuery.data ?? []
  const serverCountResult: { count?: number } | undefined =
    countServerQuery.data
  const serverTotal = serverCountResult?.count ?? serverBlogItems.length

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query(queryName, { top: topParam })
        .subscribe((result) => {
          setBlogItems(result ?? [])
          setMounted(true)
        })
      return () => close()
    },
    [client, queryName],
  )

  useEffect(() => {
    setTop(PAGE_SIZE)
    setMounted(false)
    const cleanupQuery = query(PAGE_SIZE)
    return () => {
      cleanupQuery()
    }
  }, [query])

  useEffect(() => {
    if (top === PAGE_SIZE) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query])

  const renderedBlogItems = mounted ? blogItems : serverBlogItems
  const currentTotal = serverTotal

  const { lastItemRef } = useInfiniteScroll({
    isLoading,
    hasMore: renderedBlogItems.length < currentTotal,
    loadMore: () => setTop((prev) => prev + PAGE_SIZE),
    itemCount: renderedBlogItems.length,
  })

  if (renderedBlogItems.length === 0 && !isLoading) {
    return <div>{t.app.blogPage.noContent}</div>
  }

  return (
    <>
      {isLoading ? (
        <div>{t.app.blogPage.loading}</div>
      ) : (
        <styled.div className="a b c"
          style={{
            '@media (min-width: 800px)': {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              '& > :nth-child(1)': {
                gridColumn: 'span 2',
              },
            },
          }}
        >
          {renderedBlogItems.map((blogItem, index) => {
            const card = (
              <BlogCard
                orientation={
                  index === 0 && !isMobile ? 'horizontal' : 'vertical'
                }
                image={blogItem.images?.[0]}
                meta={new Date(blogItem.publishedAt).toLocaleDateString()}
                title={blogItem.title}
                to={pages.blog(blogType, blogItem.slug)}
                imageStyle={
                  index === 0 && !isMobile
                    ? {
                        aspectRatio: '1/1',
                        width: 'auto',
                        maxHeight: '25rem',
                      }
                    : undefined
                }
                intro={index === 0 && !isMobile ? blogItem.intro : undefined}
              />
            )
            if (index === renderedBlogItems.length - 1) {
              return (
                <span key={blogItem.id} ref={lastItemRef}>
                  {card}
                </span>
              )
            }
            return <span key={blogItem.id}>{card}</span>
          })}
        </styled.div>
      )}
    </>
  )
}
